import { SearchOutlined } from '@ant-design/icons';
import { Button, Select } from 'antd';
import React, { useEffect, useState } from 'react';

export interface MultiKeywordSearchProps {
  defaultValue?: string;
  placeholder?: string;
  style?: React.CSSProperties;
  onSubmit: (q: string) => void;
  allowClear?: boolean;
}

const MultiKeywordSearch = (props: MultiKeywordSearchProps) => {
  const { defaultValue, placeholder, style, onSubmit, allowClear } = props;
  const initialChips = (defaultValue || '')
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);
  const [chips, setChips] = useState<string[]>(() =>
    Array.from(new Set(initialChips))
  );

  useEffect(() => {
    const nextChips = Array.from(
      new Set(
        (defaultValue || '')
          .split(/[,;]/)
          .map(s => s.trim())
          .filter(Boolean)
      )
    );
    const canonical = chips.join(';');
    const nextCanonical = nextChips.join(';');
    if (canonical === nextCanonical) return;
    setChips(nextChips);
    setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);
  const [searchValue, setSearchValue] = useState<string>('');

  const cleanChips = (vals: string[]) =>
    Array.from(new Set(vals.map(v => v.trim()).filter(Boolean)));

  const submit = (override?: string) => {
    const value = override !== undefined ? override : searchValue;
    const currentParts = value
      .split(/[,;]/)
      .map(s => s.trim())
      .filter(Boolean);
    const parts = cleanChips([...chips, ...currentParts]);
    onSubmit(parts.join(';'));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const current = searchValue;
      if (e.target) {
        (e.target as HTMLInputElement).value = '';
      }
      setSearchValue('');
      // prevent rc-select from turning the trailing text into an extra chip
      submit(current);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', ...style }}>
      <Select
        mode="tags"
        value={chips}
        searchValue={searchValue}
        onSearch={val => setSearchValue(val)}
        onChange={vals => setChips(cleanChips(vals as string[]))}
        onKeyDown={handleKeyDown}
        tokenSeparators={[',', ';']}
        open={false}
        dropdownStyle={{ display: 'none' }}
        placeholder={placeholder}
        allowClear={allowClear}
        onClear={() => {
          setChips([]);
          setSearchValue('');
          onSubmit('');
        }}
        style={{ flex: 1, minWidth: 120 }}
      />
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={() => submit()}
        size="middle"
      />
    </div>
  );
};

export default MultiKeywordSearch;
